import { chakra } from '@chakra-ui/react'

import { ThemeButton } from 'src/components/ThemeToggle/styles'
import { shouldForwardProp } from 'src/utils/should-forward-prop'

export const Container = chakra('div', {
    baseStyle: ({ theme }) => ({
        paddingBottom: '2px',
        backgroundColor: theme.palette.themeToggle.backgroundColor,
        borderRadius: '6px',
    }),
})

export const SectionTitle = chakra('div', {
    baseStyle: ({ theme }) => ({
        padding: '16px 16px 8px 16px',
        fontSize: '14px',
        lineHeight: '21px',
        color: theme.palette.themeToggle.textColor,

        [`@container (max-width: ${theme.breakPoints.tabletMaxWidth}px)`]: {
            padding: '0 0 10px 0',
            fontSize: '16px',
            lineHeight: '24px',
        },
    }),
})

export const ColorRadioGroup = chakra('fieldset', {
    baseStyle: {
        border: 0,
        margin: 0,
        padding: 0,
        minInlineSize: 0,
    },
})

export const ColorsSelection = chakra('div', {
    baseStyle: ({ theme }) => ({
        padding: '8px 0 16px',
        margin: '0 2px',
        backgroundColor: theme.palette.themeToggle.colorOptionsBgColor,
        width: '190px',
        display: 'flex',
        justifyContent: 'center',
        flexWrap: 'wrap',
        columnGap: '24px',
        rowGap: '8px',

        [`@container (max-width: ${theme.breakPoints.tabletMaxWidth}px)`]: {
            width: 'calc(100% + 44px)',
            position: 'relative',
            left: '-24px',
            columnGap: '12px',
            padding: '2px 24px 16px 24px',
            flexWrap: 'nowrap',
            justifyContent: 'flex-start',
            overflowX: 'scroll',
            backgroundColor: 'transparent',
        },
    }),
})

export const ColorOption = chakra('label', {
    baseStyle: ({ theme }) => ({
        width: '68px',
        fontSize: '14px',
        lineHeight: '21px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: '7px',
        color: theme.palette.themeToggle.textColor,
        cursor: 'pointer',

        // The radio input is visually hidden, so surface keyboard focus on the color indicator instead.
        '& input:focus-visible + span': {
            outline: `2px solid ${theme.palette.global.focusIndicatorColor}`,
            outlineOffset: '3px',
        },

        [`@container (max-width: ${theme.breakPoints.tabletMaxWidth}px)`]: {
            width: 'auto',
            flexDirection: 'column',
            gap: '6px',
            fontSize: '10px',
            lineHeight: '14px',
        },
    }),
})

export const VisuallyHiddenRadio = chakra('input', {
    baseStyle: {
        position: 'absolute',
        width: '1px',
        height: '1px',
        padding: 0,
        margin: '-1px',
        border: 0,
        overflow: 'hidden',
        clip: 'rect(0 0 0 0)',
        whiteSpace: 'nowrap',
    },
})

type ColorIndicatorProps = { $colorValue: string; $isActive: boolean; $stroke: boolean }
export const ColorIndicator = chakra<'span', ColorIndicatorProps>('span', {
    baseStyle: ({ $colorValue, $stroke, $isActive, theme }) => ({
        width: '12px',
        height: '12px',
        borderRadius: '50%',
        backgroundColor: $colorValue,
        border: $stroke ? '1px solid' : 'none',
        borderColor: theme.palette.themeToggle.defaultColorIndicatorStroke,
        position: 'relative',

        _before: {
            display: $isActive ? 'block' : 'none',
            content: '""',
            position: 'absolute',
            top: '-2px',
            left: '-2px',
            right: '-2px',
            bottom: '-2px',
            border: '1px solid',
            borderColor: theme.palette.themeToggle.colorIndicatorActiveColor,
            borderRadius: '50%',
        },

        [`@container (max-width: ${theme.breakPoints.tabletMaxWidth}px)`]: {
            width: '72px',
            height: '72px',
            borderRadius: '8px',
            border: '1px solid',
            borderColor: theme.palette.themeToggle.mobileColorIndicatorBorderColor,
            _before: {
                borderRadius: '8px',
                top: '-3px',
                left: '-3px',
                right: '-3px',
                bottom: '-3px',
                borderColor: theme.palette.themeToggle.mobileActiveColor,
            },
        },
    }),
    shouldForwardProp,
})

export const FontsSelection = chakra('div', {
    baseStyle: ({ theme }) => ({
        borderTop: '1px solid',
        borderBottom: '1px solid',
        borderColor: theme.palette.themeToggle.fontOptionsBorderColor,

        [`@container (max-width: ${theme.breakPoints.tabletMaxWidth}px)`]: {
            display: 'flex',
            gap: '12px',
            width: 'calc(100% + 44px)',
            position: 'relative',
            left: '-24px',
            padding: '2px 24px 18px',
            overflowX: 'scroll',
            border: 'none',
        },
    }),
})

export const FONT_OPTION_ACTIVE_CLASSNAME = 'active'
export const FontOption = chakra(ThemeButton, {
    baseStyle: ({ theme }) => ({
        [`@container (max-width: ${theme.breakPoints.tabletMaxWidth}px)`]: {
            padding: '16px 30px',
            lineHeight: '21px',
            whiteSpace: 'nowrap',
            borderRadius: '4px',
            border: '1px solid',
            borderColor: theme.palette.themeToggle.mobileButtonBorderColor,
            position: 'relative',
            [`&.${FONT_OPTION_ACTIVE_CLASSNAME}`]: {
                _before: {
                    display: 'block',
                    content: '""',
                    position: 'absolute',
                    top: '-3px',
                    left: '-3px',
                    right: '-3px',
                    bottom: '-3px',
                    borderColor: theme.palette.themeToggle.mobileActiveColor,
                    borderRadius: '4px',
                },
            },
        },
    }),
})
